/**
 * User Status Modal - חלון שינוי סטטוס משתמש
 * Change user status (activate/suspend) with reason
 */

import { useRef, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { X, AlertTriangle } from 'lucide-react'
import type { AdminUser } from '../../types'
import { useDialogA11y } from '../../hooks/useDialogA11y'

interface UserStatusModalProps {
  user: AdminUser
  onClose: () => void
  onSuccess: () => void
}

export default function UserStatusModal({
  user,
  onClose,
  onSuccess
}: UserStatusModalProps) {
  const { updateUserStatus, usersLoading } = useAdminStore()
  // Open reflecting the user's ACTUAL status, so the admin sees the real state
  // and must deliberately pick the change. Defaulting to the opposite made an
  // active user look suspended every time the modal reopened (phantom "revert"),
  // and risked accidentally suspending an active user on a no-op save.
  const [newStatus, setNewStatus] = useState<'active' | 'suspended'>(
    user.status === 'suspended' ? 'suspended' : 'active'
  )
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogA11y(true, dialogRef, onClose)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate reason for suspension
    if (newStatus === 'suspended' && reason.length < 10) {
      setError('נא לספק סיבה מפורטת - לפחות 10 תווים')
      return
    }

    try {
      await updateUserStatus(user._id, { status: newStatus, reason })
      onSuccess()
    } catch (err) {
      setError('שגיאה בעדכון סטטוס המשתמש')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-status-modal-title"
        className="bg-white rounded-lg max-w-md w-full"
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 id="user-status-modal-title" className="text-xl font-heading font-bold">שינוי סטטוס משתמש</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="סגירה"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* User Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">משתמש</p>
            <p className="font-medium">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-sm text-gray-600 font-mono">{user.email}</p>
          </div>

          {/* Current Status */}
          <div>
            <p className="text-sm text-gray-600 mb-1">סטטוס נוכחי</p>
            <p className="font-medium">
              {user.status === 'active' && 'פעיל'}
              {user.status === 'suspended' && 'מושעה'}
              {user.status === 'pending_verification' && 'ממתין לאימות'}
            </p>
          </div>

          {/* New Status */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2" id="new-status-label">
              סטטוס חדש
            </span>
            <div className="space-y-2" role="radiogroup" aria-labelledby="new-status-label">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="active"
                  checked={newStatus === 'active'}
                  onChange={(e) => setNewStatus(e.target.value as 'active')}
                  className="text-primary-600"
                  aria-label="פעיל"
                />
                <div>
                  <p className="font-medium">פעיל</p>
                  <p className="text-sm text-gray-600">
                    המשתמש יוכל להתחבר ולהשתמש במערכת
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value="suspended"
                  checked={newStatus === 'suspended'}
                  onChange={(e) => setNewStatus(e.target.value as 'suspended')}
                  className="text-primary-600"
                  aria-label="מושעה"
                />
                <div>
                  <p className="font-medium">מושעה</p>
                  <p className="text-sm text-gray-600">
                    המשתמש לא יוכל להתחבר למערכת
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label htmlFor="user-status-reason" className="block text-sm font-medium text-gray-700 mb-2">
              סיבה {newStatus === 'suspended' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              id="user-status-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="הסבר מדוע משנים את סטטוס המשתמש..."
              className="input"
              required={newStatus === 'suspended'}
            />
            <p className="text-xs text-gray-500 mt-1">
              {newStatus === 'suspended' && 'חובה לספק סיבה מפורטת - לפחות 10 תווים'}
            </p>
          </div>

          {/* Warning for Suspension */}
          {newStatus === 'suspended' && (
            <div className="alert alert-warning">
              <AlertTriangle size={20} aria-hidden="true" />
              <p className="text-sm">
                השעיית המשתמש תמנע ממנו גישה מיידית למערכת. פעולה זו תתועד ביומן
                הביקורת.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="alert alert-error" role="alert">
              <p>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={usersLoading}
            >
              ביטול
            </button>
            <button
              type="submit"
              className={`btn ${
                newStatus === 'suspended' ? 'btn-error' : 'btn-primary'
              }`}
              disabled={usersLoading}
            >
              {usersLoading ? 'מעדכן...' : 'עדכן סטטוס'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
